import axios from 'axios';
import {useEffect, useState} from 'react';
import {getDataTypeEndpointUrl} from './data-url-helpers';
import {createListEndpointHook} from './util/endpointhooks.ts';

export function addEnumOption(dataTypeId, option) {
    return axios.post(`/api/config/enum-options/${dataTypeId}`, option);
}

export function updateEnumOption(dataTypeId, enumOption) {
    return axios.put(`/api/config/${dataTypeId}/enum-options/${enumOption.enumOptionId}`, enumOption);
}

/**
 * Load all the enum options for a given type. The response is a list of objects with the following properties:
 * - enumOptionId
 * - value
 * @param enumTypeIdOrName The id or name of the enum type
 * @returns {Promise<AxiosResponse<{enumOptionId: number, value: string}>>}
 */
export function getEnumOptions(enumTypeIdOrName) {
    return axios.get(`${getDataTypeEndpointUrl(enumTypeIdOrName)}/enum-options`);
}

function loadDataTypes() {
    return axios.get(`/api/config/data-types`).then((response) => response.data);
}

export const useDataTypeList = () => {
    const [dataTypes, setDataTypes] = useState(null);

    useEffect(() => {
        loadDataTypes().then((opts) => setDataTypes(opts));
    }, []);

    return dataTypes;
};

/**
 * @return {Promise<import("../types/dataTypes").EntityDataTypeDetail[]>}
 */
function loadEntityDataTypes() {
    return axios.get(`/api/config/root-data-types`).then((response) => response.data);
}

export const useEntityDataTypesListExtended = createListEndpointHook(loadEntityDataTypes);

export function useEntityDataTypesList() {
    const [entityDataTypes, setEntityDataTypes] = useState(null);

    useEffect(() => {
        loadEntityDataTypes().then((opts) => setEntityDataTypes(opts));
    }, []);

    return entityDataTypes;
}

function loadAllDataTypesFlat() {
    return axios.get(`/api/config/table-data-types`).then((response) => response.data);
}

/**
 * Gets the root data types
 * @returns {Object} all root data types of application flattened
 */
export function useTableDataTypes() {
    const [tableDataTypes, setTableDataTypes] = useState(null);

    useEffect(() => {
        loadAllDataTypesFlat().then((opts) => setTableDataTypes(opts));
    }, []);

    return tableDataTypes;
}

export const addAttributeV2 = (entityDataTypeId, attribute) =>
    axios.post(`/api/config/v2/data-types/${entityDataTypeId}/attributes`, attribute);

export const addLiveAttributeV2 = (entityDataTypeId, attribute) =>
    axios.post(`/api/config/v2/data-types/${entityDataTypeId}/live-attributes`, attribute);

export const patchDbAttribute = (entityDataTypeName, attributeName, attributeData) =>
    axios.patch(`/api/config/v2/data-types/${entityDataTypeName}/attributes/${attributeName}`, attributeData);

export function deleteAttribute(entityTypeId, entityAttributeId) {
    return axios.delete(`/api/config/data-types/${entityTypeId}/attributes/${entityAttributeId}`);
}

export function setDefaultTableSorting(attributeId, direction) {
    return axios.post(`/api/config/data-types/set-default-table-sorting`, {
        attributeId: +attributeId,
        direction,
    });
}

export function createEnumType(enumType) {
    return axios.post(`/api/config/data-types/enum-data-type`, enumType).then((response) => response.data);
}

export function createEntityType(entityType) {
    return axios.post(`/api/config/data-types/entity-data-type`, entityType).then((response) => response.data);
}

export function updateEntityType(entityType) {
    return axios.put(`/api/config/data-types/entity-data-type`, entityType).then((response) => response.data);
}

export function createProfileType(profileType) {
    return axios.post(`/api/config/data-types/profile-entity-type`, profileType).then((response) => response.data);
}

export function updateProfileType(profileType) {
    return axios.put(`/api/config/data-types/profile-entity-type`, profileType).then((response) => response.data);
}
